import {
  Alert,
  App,
  Button,
  Descriptions,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
} from '@wanjianshi/admin-ui';
import type { ApiResponse } from '@wanjianshi/shared-contracts';
import { formatDateTime } from '@wanjianshi/shared-utils';
import { useState } from 'react';
import { useAdminAuth } from '@/auth/store';
import { useLatestRequestGuard } from '@/hooks/useLatestRequestGuard';
import {
  adminApi,
  adminOperationOptions,
  createIdempotencyKey,
  isAdminUnauthorizedError,
} from '@/services/api/client';


type RechargeRow = Record<string, unknown> & { id?: string | number };

interface ReversalFormValues {
  reversal_points: number;
  reason: string;
}

interface ReversalHistoryItem {
  id: string;
  reversal_points: string;
  reversed_points_after: string;
  remaining_points_after: string;
  balance_before: string;
  balance_after: string;
  reason: string;
  operator_nickname?: string | null;
  operator_user_id: string;
  status: string;
  create_time: string;
}

interface ReversalHistoryData {
  items: ReversalHistoryItem[];
  total: number;
  page: number;
  page_size: number;
}

function points(value: unknown): string {
  const number = Number(value ?? 0);
  return Number.isFinite(number)
    ? number.toLocaleString('zh-CN', { maximumFractionDigits: 6 })
    : '0';
}

function textValue(value: unknown): string {
  return String(value ?? '').trim();
}

function orderNumber(value: unknown) {
  const normalized = textValue(value);
  return normalized
    ? <Typography.Text copyable={{ text: normalized }}>{normalized}</Typography.Text>
    : '-';
}

function paymentMethod(value: unknown): string {
  const normalized = textValue(value).toLowerCase();
  if (!normalized) return '-';
  if (normalized.includes('alipay')) return '支付宝';
  if (normalized.includes('wechat') || normalized.includes('wx')) return '微信支付';
  return textValue(value);
}

function paymentStatus(value: unknown) {
  const label = textValue(value) || '未知状态';
  const color = label === '支付成功'
    ? 'green'
    : label === '待支付'
      ? 'blue'
      : label === '支付失败' || label === '已退款'
        ? 'red'
        : 'default';
  return <Tag color={color}>{label}</Tag>;
}

function pointReversalPath(orderId: string): string {
  return ['', 'admin', 'recharge-orders', orderId, 'point-reversals'].join('/');
}

export function RechargePointReversalActions({
  row,
  onReload,
}: {
  row: RechargeRow;
  onReload: () => Promise<void>;
}) {
  const { message } = App.useApp();
  const hasPermission = useAdminAuth(state => state.hasPermission);
  const [form] = Form.useForm<ReversalFormValues>();
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [history, setHistory] = useState<ReversalHistoryData>({
    items: [], total: 0, page: 1, page_size: 20,
  });
  const beginHistoryRequest = useLatestRequestGuard();
  const orderId = String(row.id ?? '');
  const merchantOrderId = textValue(row.merchant_order_id);
  const channelOrderNo = textValue(row.transaction_id);
  const primaryOrderNumber = merchantOrderId || channelOrderNo || orderId;
  const userNickname = textValue(row.user_nickname);
  const userMobile = textValue(row.user_mobile_masked);
  const userId = textValue(row.user_id);
  const maxReversible = Number(row.max_reversible_points ?? 0);
  const canReverse = Boolean(row.can_point_reverse) && maxReversible > 0;
  const disabledReason = String(
    row.point_reversal_disabled_reason || '当前订单不能执行积分冲正',
  );

  const loadHistory = async (page = 1, pageSize = history.page_size) => {
    const isLatest = beginHistoryRequest();
    setHistoryLoading(true);
    try {
      const response = await adminApi.get<ApiResponse<ReversalHistoryData>>(
        pointReversalPath(orderId),
        { params: { page, page_size: pageSize } },
      );
      if (!isLatest()) return;
      setHistory(response.data);
    } catch (error) {
      if (!isLatest()) return;
      if (isAdminUnauthorizedError(error)) return;
      message.error(error instanceof Error ? error.message : '冲正记录加载失败');
    } finally {
      if (isLatest()) setHistoryLoading(false);
    }
  };

  const submit = async () => {
    const values = await form.validateFields();
    const reason = values.reason.trim();
    const key = createIdempotencyKey('point-reversal');
    setSubmitting(true);
    try {
      await adminApi.post(
        pointReversalPath(orderId),
        {
          reversal_points: String(values.reversal_points),
          reason,
          idempotency_key: key,
        },
        adminOperationOptions(reason, key),
      );
      message.success('积分冲正成功');
      setModalOpen(false);
      form.resetFields();
      await onReload();
      if (historyOpen) await loadHistory(1, history.page_size);
    } catch (error) {
      if (isAdminUnauthorizedError(error)) return;
      message.error(error instanceof Error ? error.message : '积分冲正失败');
    } finally {
      setSubmitting(false);
    }
  };

  const historyColumns = [
    { title: '冲正时间', dataIndex: 'create_time', width: 170, render: formatDateTime },
    { title: '本次冲正', dataIndex: 'reversal_points', width: 110, render: points },
    { title: '累计冲正', dataIndex: 'reversed_points_after', width: 110, render: points },
    { title: '订单剩余', dataIndex: 'remaining_points_after', width: 110, render: points },
    { title: '冲正前余额', dataIndex: 'balance_before', width: 120, render: points },
    { title: '冲正后余额', dataIndex: 'balance_after', width: 120, render: points },
    {
      title: '操作人', width: 150,
      render: (_: unknown, item: ReversalHistoryItem) => item.operator_nickname || item.operator_user_id,
    },
    { title: '原因', dataIndex: 'reason', width: 240, ellipsis: true },
    { title: '状态', dataIndex: 'status', width: 90, render: (value: string) => <Tag color="green">{value}</Tag> },
  ];

  return (
    <>
      {hasPermission('wjs-admin:recharge:point-reversal') ? (
        <Tooltip title={canReverse ? undefined : disabledReason}>
          <span>
            <Button
              size="small"
              danger
              disabled={!canReverse}
              onClick={() => {
                form.resetFields();
                setModalOpen(true);
              }}
            >
              积分冲正
            </Button>
          </span>
        </Tooltip>
      ) : null}
      {hasPermission('wjs-admin:recharge:point-reversal-query') ? (
        <Button
          size="small"
          onClick={() => {
            setHistoryOpen(true);
            void loadHistory(1, 20);
          }}
        >
          冲正记录
        </Button>
      ) : null}

      <Modal
        title={`充值积分冲正 · ${primaryOrderNumber}`}
        open={modalOpen}
        okText="确认扣回积分"
        cancelText="取消"
        okButtonProps={{ danger: true, loading: submitting }}
        onOk={() => void submit()}
        onCancel={() => {
          if (!submitting) setModalOpen(false);
        }}
        destroyOnClose
        width={620}
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Alert
            type="warning"
            showIcon
            title="该操作不会向用户退还支付金额，只会扣回用户账户中的积分。"
            description="请先核对支付订单号、充值用户、支付金额和支付时间，确认无误后再执行。"
          />
          <Descriptions
            title="支付订单核对"
            bordered
            size="small"
            column={2}
            items={[
              { key: 'merchant-order', label: '商户订单号', span: 2, children: orderNumber(merchantOrderId) },
              { key: 'channel-order', label: '渠道流水号', span: 2, children: orderNumber(channelOrderNo) },
              {
                key: 'user', label: '充值用户',
                children: (
                  <Space direction="vertical" size={0}>
                    <Typography.Text strong>{userNickname || '未设置昵称'}</Typography.Text>
                    <Typography.Text type="secondary">{userMobile || '-'} · 用户 ID：{userId || '-'}</Typography.Text>
                  </Space>
                ),
              },
              {
                key: 'amount', label: '支付金额',
                children: <Typography.Text strong>¥ {Number(row.amount_yuan ?? 0).toFixed(2)}</Typography.Text>,
              },
              { key: 'subject', label: '订单内容', children: textValue(row.subject) || '-' },
              { key: 'payment-status', label: '支付状态', children: paymentStatus(row.payment_status) },
              { key: 'payment-method', label: '支付渠道', children: paymentMethod(row.payment_method) },
              { key: 'success-time', label: '支付成功时间', children: formatDateTime(row.success_time as string | number | null) },
            ]}
          />
          <Descriptions
            title="积分核对"
            bordered
            size="small"
            column={2}
            items={[
              { key: 'credited', label: '原到账积分', children: points(row.credited_points) },
              { key: 'reversed', label: '累计已冲正', children: points(row.reversed_points) },
              { key: 'remaining', label: '订单剩余积分', children: points(row.remaining_reversible_points) },
              { key: 'balance', label: '账户余额', children: points(row.account_balance) },
              { key: 'frozen', label: '冻结积分', children: points(row.frozen_balance) },
              { key: 'available', label: '当前可用积分', children: points(row.available_points) },
              {
                key: 'max', label: '本次最多可冲正', span: 2,
                children: <Typography.Text strong type="danger">{points(row.max_reversible_points)}</Typography.Text>,
              },
            ]}
          />
          <Form form={form} layout="vertical">
            <Form.Item
              name="reversal_points"
              label="本次冲正积分"
              rules={[
                { required: true, message: '请输入本次冲正积分' },
                {
                  validator: async (_, value) => {
                    const normalized = Number(value);
                    if (!Number.isFinite(normalized) || normalized <= 0) {
                      throw new Error('冲正积分必须大于 0');
                    }
                    if (normalized > maxReversible) {
                      throw new Error(`本次最多可冲正 ${points(maxReversible)} 积分`);
                    }
                  },
                },
              ]}
            >
              <InputNumber
                min={0.000001}
                max={maxReversible}
                precision={6}
                step={1}
                style={{ width: '100%' }}
                placeholder={`最多 ${points(maxReversible)} 积分`}
              />
            </Form.Item>
            <Form.Item
              name="reason"
              label="操作原因"
              rules={[
                { required: true, whitespace: true, message: '请填写积分冲正原因' },
                { max: 500, message: '操作原因不能超过 500 个字符' },
              ]}
            >
              <Input.TextArea rows={4} maxLength={500} showCount placeholder="请填写可审计的积分冲正原因" />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <Drawer
        title={`积分冲正记录 · ${primaryOrderNumber}`}
        size={1080}
        open={historyOpen}
        onClose={() => {
          beginHistoryRequest();
          setHistoryOpen(false);
          setHistoryLoading(false);
        }}
      >
        <Descriptions
          bordered
          size="small"
          column={2}
          style={{ marginBottom: 20 }}
          items={[
            { key: 'merchant-order', label: '商户订单号', children: orderNumber(merchantOrderId) },
            { key: 'channel-order', label: '渠道流水号', children: orderNumber(channelOrderNo) },
            { key: 'user', label: '充值用户', children: `${userNickname || '-'} / ${userMobile || '-'}` },
            { key: 'amount', label: '支付金额', children: `¥ ${Number(row.amount_yuan ?? 0).toFixed(2)}` },
            { key: 'success-time', label: '支付成功时间', children: formatDateTime(row.success_time as string | number | null) },
          ]}
        />
        <Table<ReversalHistoryItem>
          rowKey="id"
          columns={historyColumns}
          dataSource={history.items}
          loading={historyLoading}
          pagination={false}
          scroll={{ x: 1250 }}
        />
        <Pagination
          style={{ marginTop: 16, textAlign: 'right' }}
          current={history.page}
          pageSize={history.page_size}
          total={history.total}
          onChange={(page, pageSize) => void loadHistory(page, pageSize)}
        />
      </Drawer>
    </>
  );
}
